import type { QuestView } from '@xox/shared'
import { Check, ChevronRight, Scroll } from 'lucide-react'
import { Link } from 'react-router'
import { QUEST_HOME } from '../../lib/games'
import { Card } from '../ui'

/** Today's three quests: a meter each, the XP on offer, a tick when done; each links to its game. */
export function QuestList({ quests }: { quests: QuestView[] | null }) {
  const done = quests?.filter((q) => q.done).length ?? 0
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-semibold">
          <Scroll className="mr-1 inline size-4 text-amber-500" aria-hidden /> Today's quests
        </h2>
        {quests && (
          <span className="text-xs font-semibold tabular-nums text-slate-500">
            {done}/{quests.length} done
          </span>
        )}
      </div>
      {quests ? (
        <ul className="flex flex-col gap-2">
          {quests.map((q) => (
            <li key={q.id}>
              <Link
                to={QUEST_HOME[q.metric]}
                className="-mx-2 flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <span
                  className={[
                    'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-black tabular-nums',
                    q.done
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
                  ].join(' ')}
                  aria-hidden
                >
                  {q.done ? <Check className="size-4" /> : q.goal - q.progress}
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className={`truncate text-sm font-semibold ${q.done ? 'line-through opacity-60' : ''}`}
                  >
                    {q.title}
                  </div>
                  <div
                    className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={q.goal}
                    aria-valuenow={q.progress}
                    aria-label={q.title}
                  >
                    <div
                      className={`h-full rounded-full transition-[width] duration-500 ${q.done ? 'bg-emerald-500' : 'bg-amber-400'}`}
                      style={{ width: `${(q.progress / q.goal) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black tabular-nums text-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
                  +{q.xp} XP
                </span>
                <ChevronRight className="size-4 shrink-0 text-slate-400" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">Loading today's quests…</p>
      )}
    </Card>
  )
}
